<template>
    <div style="height:100%" class="white">
        <table class="report-table white">

            <thead class="report-header">
                <tr>
                    <th class="print-only">
                        <!-- will print at the top of every page -->
                        <!-- this is a placeholder component - it is only here to give spacing on every printed page so that the actual site header has space - see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
                        &nbsp;
                    </th>
                </tr>
            </thead>

            <tbody>
                <tr>
                    <td>
                        <v-progress-linear color="primary" indeterminate v-if="loading"></v-progress-linear>
                        <v-container v-else>

                            <v-row>
                                <v-col class="text-center">
                                    <span class="font-weight-bold text-h4">SPECIES BATCH POOL {{ speciesBatchPool.Barcode }}</span>
                                </v-col>
                            </v-row>

                            <v-row no-gutters>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Created:</span> {{ speciesBatchPool.CreatedTime | formatDate }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">By:</span> {{ speciesBatchPool.CreatedByEmail | formatUserName(speciesBatchPool.CreatedByFullName, "") }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Closed:</span> {{ speciesBatchPool.ClosedTime | formatDate }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">By:</span> {{ speciesBatchPool.ClosedByEmail | formatUserName(speciesBatchPool.ClosedByFullName, "") }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Species:</span> {{ speciesBatchPool.SpeciesFullName }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Total Weight:</span> {{ speciesBatchPoolTotalWeight || 0 }}g
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12">
                                    <span class="font-weight-bold text-h6">Monthly Pools in Species Batch Pool</span>
                                    <v-simple-table>
                                        <template v-slot:default>
                                            <thead>
                                                <tr>
                                                    <th class="text-left">
                                                        Barcode
                                                    </th>
                                                    <th class="text-left">
                                                        Species
                                                    </th>
                                                    <th class="text-left">
                                                        Weight
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody v-if="speciesBatchPool.MonthlyPools.length !== 0">
                                                <tr v-for="p in speciesBatchPool.MonthlyPools" :key="p.MonthlyPoolID">
                                                    <td>{{ p.Barcode }}</td>
                                                    <td>{{ p.SpeciesFullName }}</td>
                                                    <td>{{ p.TotalWeight + "g" }}</td>
                                                </tr>
                                            </tbody>
                                            <tbody v-else>
                                                <tr>
                                                    <td colspan="5">
                                                        There are no monthly pools in this species batch pool
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <span class="font-weight-bold text-h6">Venom Analysis</span>
                                    <PrintHistoricalAnalysis :venomAnalysis="venomAnalysis"></PrintHistoricalAnalysis>
                                </v-col>
                            </v-row>
                        </v-container>
                    </td>
                </tr>
            </tbody>

            <tfoot class="report-footer">
                <tr>
                    <td class="print-only">
                        <!-- will print at the bottom of every page -->
                        <!-- this is a placeholder component - it is only here to give spacing on every printed page so that the actual site footer has space - see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
                        &nbsp;
                    </td>
                </tr>
            </tfoot>

            <!-- this will be fixed to the bottom of the page, and repeated on each printed page; to achieve the proper spacing, see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
            <div class="print-footer-container print-only">
                Report generated by {{name}} at {{$moment().format($constants.DATE_FORMAT)}}
            </div>

        </table>
    </div>
</template>

<script>
import util from '@/helpers/util';
import moment from "moment";
import constants from "comunet-btg-utah-repository-shared/constants";
import * as venomPoolingAPI from "@/services/venomPoolingAPI";
import * as venomAnalysisAPI from '@/services/venomAnalysisAPI';

import PrintHistoricalAnalysis from '@/components/venomanalysis/PrintHistoricalAnalysis.vue';

export default {
    components: {
        PrintHistoricalAnalysis
    },
    props: {
        speciesBatchPoolID: {
            type: [ Number, String ],
            default: null
        }
    },
    data() {
        return {
            loading: false,
            venomAnalysis: [],
            speciesBatchPool: {}
        }
    },
    computed: {
        name: function () {
            return util.getUserProfileNameFromUserObject(this.$store.state.authStore.user);
        },
        speciesBatchPoolTotalWeight() {
            let weight = 0;

            if (this.speciesBatchPool && this.speciesBatchPool.MonthlyPools) {
                for (const monthlyPool of this.speciesBatchPool.MonthlyPools) {
                    weight = weight + (monthlyPool.TotalWeight || 0);
                }
            }

            return weight;
        }
    },
    async created() {
        this.loading = true;

        this.speciesBatchPool = await venomPoolingAPI.getSpeciesBatchPools({ speciesBatchPoolID: this.speciesBatchPoolID });
        this.venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, -1, -1, -1, this.speciesBatchPoolID);

        this.loading = false;

        this.$nextTick(() => window.print());
    },
    filters: {
        formatDate: function (value) {
            if (!value) {
                return "";
            }

            var date = moment(value);
            if (date.isValid) {
                return date.format(constants.DATE_FORMAT);
            } else {
                return value;
            }
        },
        formatUserName(email, firstname, surname) {
            if (!email) {
                return "";
            }

            var desc = "";
            if (firstname && firstname != "") {
                desc = firstname;
            }

            if (surname && surname != "") {
                desc += (desc == "" ? "" : " ") + surname;
            }

            desc += (desc == "" ? "" : " (") + email + (desc == "" ? "" : ")");

            return desc;
        },
    }
}
</script>

<style lang="scss">
#app {
    .report-table {
        width: 100%;
    }

    // this is a placeholder - if it is needed then we'll need to change the height
    .report-header th {
        height: 0px;
    }

    .report-footer td, .print-footer-container {
        height: 50px; // this reserves space for the actual footer, see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a
    }

    .print-footer-container {
        position: fixed;
        bottom: 0px;
        width: 100%;
        text-align: center;
        color: var(--v-lightgray-base);
    }
}

@media print {
    #app {
        // shade every second row
        .v-data-table__wrapper table tr:nth-child(2n) {
            background-color: var(--v-lightgray-lighten1)
        }
    }
}
</style>